import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Shield as ShieldIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';

const AlertsDecisionGuide = ({ open, onClose }) => {
  const [expandedSection, setExpandedSection] = useState('cas1');

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getResponsibilityIcon = (responsibility) => {
    switch (responsibility) {
      case 'OWNER_FRAUD_RISK':
      case 'OWNER_CRITICAL':
      case 'OWNER': return <PersonIcon color="error" />;
      case 'RENTER': return <PersonIcon color="warning" />;
      case 'BOTH_PARTIES': return <GavelIcon color="info" />;
      case 'SYSTEM': return <ScheduleIcon color="action" />;
      default: return <InfoIcon />;
    }
  };

  const casesData = [
    {
      id: 'cas1',
      title: 'CAS 1 - NO_PICKUP : Réservation confirmée non récupérée',
      severity: 'critical',
      description: 'Client a payé mais le matériel n\'a pas été récupéré',
      scenarios: [
        {
          condition: 'Paiement en ligne + Pickup NON confirmé',
          protection: 'MAXIMUM',
          responsibility: 'OWNER_FRAUD_RISK',
          urgency: 'CRITIQUE',
          action: 'AUTO_REFUND_CLIENT',
          example: '❌ Client a payé 500€ en ligne pour un drone, mais le propriétaire ne confirme pas le pickup après 2h de retard',
          decision: 'REMBOURSEMENT AUTOMATIQUE + DISPUTE contre propriétaire',
          justification: 'Le client est protégé car il a payé mais n\'a pas reçu le service. Risque de fraude du propriétaire.',
          timeframe: '1-2h après heure prévue'
        },
        {
          condition: 'Paiement en ligne + Pickup CONFIRMÉ',
          protection: 'MEDIUM',
          responsibility: 'OWNER_CRITICAL',
          urgency: 'HAUTE',
          action: 'CONTACT_OWNER_URGENT',
          example: '⚠️ Client payé + pickup confirmé mais réservation toujours en "confirmed" au lieu de "active"',
          decision: 'CONTACTER PROPRIÉTAIRE en urgence pour activer la réservation',
          justification: 'Problème technique probable. Le pickup est fait mais le système n\'a pas mis à jour le statut.',
          timeframe: 'Immédiatement'
        },
        {
          condition: 'Paiement cash planifié',
          protection: 'LOW',
          responsibility: 'BOTH_PARTIES',
          urgency: 'MOYENNE',
          action: 'VERIFY_BOTH_PRESENCE',
          example: '🤝 Réservation en cash, les deux parties doivent se rencontrer',
          decision: 'VÉRIFIER la présence des deux parties',
          justification: 'Responsabilité partagée car le paiement n\'est pas encore effectué.',
          timeframe: '2h après heure prévue'
        }
      ]
    },
    {
      id: 'cas2',
      title: 'CAS 2 - NO_RETURN : Réservation active non terminée',
      severity: 'high',
      description: 'Matériel non rendu à la fin de la période de location',
      scenarios: [
        {
          condition: 'Retour NON confirmé + Client injoignable (72h)',
          protection: 'NONE',
          responsibility: 'RENTER',
          urgency: 'HAUTE',
          action: 'CONFISQUER_CAUTION_AUTO',
          example: '🚨 Drone loué 3 jours, retour non confirmé, client ne répond plus depuis 4 jours',
          decision: 'CONFISCATION JUSTIFIÉE de la caution',
          justification: 'Le client a eu le matériel mais ne l\'a pas rendu et est injoignable. Présomption de vol.',
          timeframe: '72h après fin location + 72h inactivité'
        },
        {
          condition: 'Retour NON confirmé + Client joignable',
          protection: 'LOW',
          responsibility: 'RENTER',
          urgency: 'MOYENNE',
          action: 'OPEN_DISPUTE_CLARIFICATION',
          example: '❓ Appareil photo non rendu mais client répond aux messages',
          decision: 'OUVRIR DISPUTE pour clarification',
          justification: 'Situation ambiguë à résoudre par médiation entre les parties.',
          timeframe: '24h après fin location'
        },
        {
          condition: 'Retour CONFIRMÉ + Statut non mis à jour',
          protection: 'HIGH',
          responsibility: 'SYSTEM',
          urgency: 'BASSE',
          action: 'AUTO_COMPLETE_TECHNICAL',
          example: '✅ Matériel rendu et confirmé mais réservation toujours "active"',
          decision: 'COMPLÉTION AUTOMATIQUE (problème technique)',
          justification: 'Le retour est confirmé, c\'est un bug système à corriger automatiquement.',
          timeframe: 'Immédiatement'
        }
      ]
    },
    {
      id: 'cas5',
      title: 'CAS 5 - PHANTOM : Réservations fantômes',
      severity: 'low',
      description: 'Réservations en pending expirées',
      scenarios: [
        {
          condition: 'Pending + 48h dépassées',
          protection: 'NONE',
          responsibility: 'SYSTEM',
          urgency: 'BASSE',
          action: 'AUTO_EXPIRE',
          example: '🕒 Réservation en "pending" depuis 3 jours sans paiement',
          decision: 'EXPIRATION AUTOMATIQUE',
          justification: 'Nettoyage système des réservations abandonnées.',
          timeframe: '48h après expiration'
        }
      ]
    }
  ];

  const protectionLevels = [
    { level: 'MAXIMUM', color: 'error', description: 'Protection totale client - Remboursement prioritaire' },
    { level: 'HIGH', color: 'warning', description: 'Protection élevée - Résolution favorable au client' },
    { level: 'MEDIUM', color: 'info', description: 'Protection modérée - Analyse cas par cas' },
    { level: 'LOW', color: 'success', description: 'Protection minimale - Responsabilité partagée' },
    { level: 'NONE', color: 'default', description: 'Aucune protection - Sanctions justifiées' }
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShieldIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">
            Guide de Décision - Gestion des Alertes
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        {/* Section Niveaux de Protection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShieldIcon />
            Niveaux de Protection Client
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {protectionLevels.map((level) => (
              <Chip
                key={level.level}
                label={level.level}
                color={level.color}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
          <List dense>
            {protectionLevels.map((level) => (
              <ListItem key={level.level} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 35 }}>
                  <Chip 
                    label={level.level} 
                    color={level.color} 
                    size="small" 
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={level.description}
                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Cas par Cas */}
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GavelIcon />
          Cas de Figures et Décisions
        </Typography>

        {casesData.map((casItem) => (
          <Accordion
            key={casItem.id}
            expanded={expandedSection === casItem.id}
            onChange={handleAccordionChange(casItem.id)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Chip 
                  label={casItem.severity.toUpperCase()} 
                  color={getSeverityColor(casItem.severity)}
                  size="small"
                />
                <Typography variant="subtitle1" fontWeight="medium">
                  {casItem.title}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 2 }}>
                {casItem.description}
              </Alert>

              {casItem.scenarios.map((scenario, index) => (
                <Card key={index} sx={{ mb: 2 }} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {getResponsibilityIcon(scenario.responsibility)}
                      <Typography variant="h6" color="primary">
                        {scenario.condition}
                      </Typography>
                      <Chip 
                        label={`Protection: ${scenario.protection}`}
                        color={scenario.protection === 'MAXIMUM' ? 'error' : 
                               scenario.protection === 'HIGH' ? 'warning' :
                               scenario.protection === 'MEDIUM' ? 'info' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Exemple Concret :</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                          {scenario.example}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Décision Recommandée :</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ p: 1, bgcolor: 'primary.50', borderRadius: 1 }}>
                          {scenario.decision}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Justification :</strong>
                      </Typography>
                      <Typography variant="body2">
                        {scenario.justification}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Chip 
                        icon={<ScheduleIcon />}
                        label={`Délai: ${scenario.timeframe}`}
                        color="default"
                        variant="outlined"
                        size="small"
                      />
                      <Chip 
                        icon={<WarningIcon />}
                        label={`Urgence: ${scenario.urgency}`}
                        color={scenario.urgency === 'CRITIQUE' ? 'error' : 
                               scenario.urgency === 'HAUTE' ? 'warning' : 'default'}
                        variant="outlined"
                        size="small"
                      />
                      <Chip 
                        icon={<PersonIcon />}
                        label={`Responsable: ${scenario.responsibility}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Section Conseils Généraux */}
        <Box sx={{ mt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>⚠️ Conseils Généraux de Gestion :</strong>
            </Typography>
            <List dense>
              <ListItem sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 20 }}>•</ListItemIcon>
                <ListItemText primary="Toujours prioriser la protection du client qui a payé en ligne" />
              </ListItem>
              <ListItem sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 20 }}>•</ListItemIcon>
                <ListItemText primary="Documenter chaque décision avec captures d'écran et justifications" />
              </ListItem>
              <ListItem sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 20 }}>•</ListItemIcon>
                <ListItemText primary="Contacter les parties avant de prendre des mesures drastiques" />
              </ListItem>
              <ListItem sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 20 }}>•</ListItemIcon>
                <ListItemText primary="Utiliser les disputes pour les cas ambigus nécessitant médiation" />
              </ListItem>
            </List>
          </Alert>

          <Alert severity="info">
            <Typography variant="subtitle2" gutterBottom>
              <strong>📞 En cas de doute :</strong>
            </Typography>
            <Typography variant="body2">
              Contactez l'équipe de direction avant de prendre des décisions sur les cas CRITIQUES 
              impliquant des montants élevés ("200€") ou des clients récurrents.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary" size="large">
          Compris, Fermer le Guide
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertsDecisionGuide;